import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, LinearProgress, MenuItem, Paper, Stack, Switch, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { nanoid } from 'nanoid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FolderIcon from '@mui/icons-material/Folder';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import AwardedBudgetCard, { AwardQuotationPicker, defaultAwardQuotationId } from './AwardedBudgetCard';
import { awardFigures } from '../../utils/calcsheet/awardFigures';
import { useQuotationStore } from '../../store/quotationStore';
import { computeTotals, PHP } from '../../utils/calcsheet/calc';
import { blurNumberInputOnWheel, parseLenientFloat } from '../../utils/calcsheet/numberInput';
import type { ProjectStatus, Quotation, QuotationKind, QuotationScopeCategory, OpportunityGrade } from '../../types/Quotation';
import { PROJECT_STATUSES, projectStatusLabel, OPPORTUNITY_GRADES, opportunityGradeLabel } from '../../types/Quotation';
import { parseLegacyWorkbook } from '../../utils/calcsheet/legacyImport';
import type { ParsedProject, ParsedQuotation } from '../../utils/calcsheet/legacyImport';
import { parseLegacyPdf } from '../../utils/calcsheet/legacyPdfImport';
import type { ParsedLegacyPdf } from '../../utils/calcsheet/legacyPdfImport';
import { useOneDriveAuth } from '../../contexts/OneDriveAuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { isCorporateOneDriveConfigured } from '../../config/onedriveConfig';
import {
  ensureProposalFolder,
  ensureWonExecutionLayout,
  ensureExecutionFolder,
  resolveCorporateDriveId,
  verifyDriveItem,
  resolveSharingUrl,
  listFoldersWithPrefix,
  listChildrenById,
  getOrCreateChildFolderById,
  uploadFileToFolderById,
  deleteDriveItem,
  projectCodePrefix,
  type DriveItemRef,
} from '../../services/onedriveFolderService';
import { onedriveConfig } from '../../config/onedriveConfig';
import LinkIcon from '@mui/icons-material/Link';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import DuplicateQuotationDialog from './DuplicateQuotationDialog';
import type { CustomerPO } from '../../types/Quotation';

export default function ProjectDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const project = useQuotationStore((s) => s.projects.find((p) => p.id === id));
  const clients = useQuotationStore((s) => s.clients);
  const allQuotations = useQuotationStore((s) => s.quotations);
  const quotations = useMemo(() => allQuotations.filter((q) => q.projectId === id), [allQuotations, id]);
  const createQuotation = useQuotationStore((s) => s.createQuotation);
  const deleteQuotation = useQuotationStore((s) => s.deleteQuotation);
  const importQuotation = useQuotationStore((s) => s.importQuotation);
  const updateProject = useQuotationStore((s) => s.updateProject);
  const syncMainProject = useQuotationStore((s) => s.syncMainProject);

  // OneDrive corporate folder integration
  const { isAuthenticated: oneDriveSignedIn, login: oneDriveLogin, getAccessToken: getOneDriveToken } = useOneDriveAuth();
  const { user: currentUser } = useAuth();
  const [oneDriveBusy, setOneDriveBusy] = useState<'proposal' | 'execution' | null>(null);
  const [oneDriveErr, setOneDriveErr] = useState<string>('');

  // Customer PO attach dialog
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [poFile, setPoFile] = useState<File | null>(null);
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [poBusy, setPoBusy] = useState(false);
  const [poErr, setPoErr] = useState('');
  const [poDeleteTarget, setPoDeleteTarget] = useState<CustomerPO | null>(null);
  const [poDeleteBusy, setPoDeleteBusy] = useState(false);

  // Ad-hoc "drop any file into the project folder" uploads
  const projectFilesInputRef = useRef<HTMLInputElement | null>(null);
  const [projectFilesBusy, setProjectFilesBusy] = useState(false);
  const [projectFilesProgress, setProjectFilesProgress] = useState<{ done: number; total: number } | null>(null);
  const [projectFilesErr, setProjectFilesErr] = useState('');
  const [projectFilesDragOver, setProjectFilesDragOver] = useState(false);
  const [uploadedProjectFiles, setUploadedProjectFiles] = useState<{ name: string; url?: string }[]>([]);

  // In-app OneDrive folder browser (read-only, navigable into subfolders)
  const [fbOpen, setFbOpen] = useState(false);
  const [fbStack, setFbStack] = useState<{ id: string; name: string }[]>([]);
  const [fbItems, setFbItems] = useState<DriveItemRef[]>([]);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbErr, setFbErr] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Quotation | null>(null);
  // Portrait phones: quotations become cards and the dense dialogs go full-screen.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [duplicateTarget, setDuplicateTarget] = useState<Quotation | null>(null);
  // Non-error info message shown next to the OneDrive buttons. Used to tell the
  // user when auto-detect matched an existing historical folder (instead of
  // creating a new one) so they don't think the system silently misbehaved.
  const [oneDriveInfo, setOneDriveInfo] = useState<string>('');
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  // Awarded-price lock: which quotation the project was won on.
  const [awardPickId, setAwardPickId] = useState('');
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);
  const [awardBusy, setAwardBusy] = useState(false);
  const [awardRefresh, setAwardRefresh] = useState(0);
  const awardQuotation = useQuotationStore((s) => s.awardQuotation);
  const unawardProject = useQuotationStore((s) => s.unawardProject);
  const [mainSyncBusy, setMainSyncBusy] = useState(false);
  const [mainSyncErr, setMainSyncErr] = useState('');
  const [mainSyncInfo, setMainSyncInfo] = useState('');

  // Link-to-existing-project dialog
  const [linkExistingOpen, setLinkExistingOpen] = useState(false);
  const [linkExistingWon, setLinkExistingWon] = useState(false);
  const [linkExistingQuery, setLinkExistingQuery] = useState('');
  const [linkExistingProjects, setLinkExistingProjects] = useState<{ id: string; project_no: string; project_name: string; account_name: string }[]>([]);
  const [linkExistingLoading, setLinkExistingLoading] = useState(false);
  const [linkExistingBusy, setLinkExistingBusy] = useState(false);

  const openLinkExistingDialog = (fromWon = false) => {
    setLinkExistingWon(fromWon);
    setLinkExistingQuery('');
    setLinkExistingOpen(true);
    setLinkExistingLoading(true);
    fetch('/api/projects')
      .then((r) => r.json())
      .then((rows: { id: string; project_no: string; project_name: string; account_name: string }[]) => {
        setLinkExistingProjects(rows);
      })
      .catch(() => setLinkExistingProjects([]))
      .finally(() => setLinkExistingLoading(false));
  };

  const linkToExistingProject = async (mainProjectId: string) => {
    if (!project) return;
    setLinkExistingBusy(true);
    setMainSyncErr('');
    setMainSyncInfo('');
    try {
      const res = await fetch(`/api/calcsheet/projects/${project.id}/link-existing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('netpacific_token') || ''}` },
        body: JSON.stringify({ mainProjectId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to link project');
      const patch: Record<string, unknown> = {
        mainProjectId: data.mainProjectId,
        mainProjectNo: data.projectNo,
        mainProjectSyncStatus: 'linked',
        ...(linkExistingWon ? { status: 'won' } : {}),
      };
      await updateProject(project.id, patch);
      setMainSyncInfo(`Linked to Project List record ${data.projectNo || data.mainProjectId}.`);
      setLinkExistingOpen(false);
      setAwardRefresh((n) => n + 1);
    } catch (e) {
      setMainSyncErr(e instanceof Error ? e.message : 'Failed to link project');
    } finally {
      setLinkExistingBusy(false);
    }
  };

  const syncToMainProject = async (force = false) => {
    if (!project) return null;
    setMainSyncBusy(true);
    setMainSyncErr('');
    setMainSyncInfo('');
    try {
      // Default (force=false): push Sales contract amount to Project List without
      // wiping status/billing. force=true full-remaps the Project List row.
      const result = await syncMainProject(project.id, { force });
      const verb = result.action === 'amount-synced'
        ? 'Synced contract amount to Project List'
        : result.action === 'linked-existing'
          ? 'Linked existing Project List record'
          : result.action === 'recreated'
            ? 'Recreated Project List record'
            : result.action === 'updated'
              ? 'Updated Project List record'
              : 'Created Project List record';
      const prevNote =
        result.action === 'amount-synced' &&
        result.previousAmount != null &&
        Math.abs((result.previousAmount || 0) - (result.amount || 0)) > 1
          ? ` (was ${PHP(result.previousAmount)})`
          : '';
      setMainSyncInfo(
        `${verb} from ${project.awarded ? 'the awarded ' : ''}${result.quotationKind} quotation: ${PHP(result.amount)}${prevNote}.`,
      );
      setAwardRefresh((n) => n + 1);
      return result;
    } catch (e) {
      setMainSyncErr(e instanceof Error ? e.message : 'Failed to sync Project List record');
      return null;
    } finally {
      setMainSyncBusy(false);
    }
  };

  const changeStatus = async (nextStatus: ProjectStatus) => {
    if (!project) return;
    setMainSyncErr('');
    setMainSyncInfo('');
    if (nextStatus === 'won' && project.status !== 'won') {
      setAwardPickId(defaultAwardQuotationId(project, quotations));
      setStatusConfirmOpen(true);
      return;
    }
    await updateProject(project.id, { status: nextStatus });
  };

  // Freeze the awarded quotation's price/budget (and push it to a linked Project List row).
  const lockAward = async (quotationId: string): Promise<boolean> => {
    if (!project || !quotationId) return false;
    setAwardBusy(true);
    setMainSyncErr('');
    setMainSyncInfo('');
    try {
      const { awarded, mainProject } = await awardQuotation(project.id, quotationId);
      const moved = mainProject && Math.abs(mainProject.previousAmount - mainProject.amount) >= 1
        ? ` Project List contract updated from ${PHP(mainProject.previousAmount)} to ${PHP(mainProject.amount)}.`
        : mainProject ? ' Project List contract matches.' : '';
      setMainSyncInfo(`Locked ${awarded.kind} rev ${awarded.revision} at ${PHP(awarded.price)} (budget ${PHP(awarded.budget)}).${moved}`);
      setAwardRefresh((n) => n + 1);
      return true;
    } catch (e) {
      setMainSyncErr(e instanceof Error ? e.message : 'Failed to lock the awarded price');
      return false;
    } finally {
      setAwardBusy(false);
    }
  };

  const confirmUnlock = async () => {
    if (!project) return;
    setUnlockConfirmOpen(false);
    setAwardBusy(true);
    setMainSyncErr('');
    try {
      await unawardProject(project.id);
      setMainSyncInfo('Awarded price unlocked. The quotation can be edited again; the Project List amount is unchanged.');
      setAwardRefresh((n) => n + 1);
    } catch (e) {
      setMainSyncErr(e instanceof Error ? e.message : 'Failed to unlock');
    } finally {
      setAwardBusy(false);
    }
  };

  const confirmWon = async (sync: boolean) => {
    if (!project) return;
    setStatusConfirmOpen(false);
    setMainSyncErr('');
    setMainSyncInfo('');
    try {
      if (awardPickId && !(await lockAward(awardPickId))) return;
      const result = sync ? await syncToMainProject(false) : null;
      if (sync && !result) return;
      await updateProject(project.id, {
        status: 'won',
        ...(result ? { mainProjectId: result.mainProjectId, mainProjectNo: result.projectNo } : {}),
      });
      openPoDialog();
    } catch (e) {
      setMainSyncErr(e instanceof Error ? e.message : 'Failed to mark project as won');
    }
  };

  const openPoDialog = () => {
    setPoFile(null);
    setPoNumber('');
    setPoDate('');
    setPoErr('');
    setPoDialogOpen(true);
  };

  // Collision-safe filename within the "Customer PO" folder: keep the original
  // name when free, else append " (2)", " (3)", ... before the extension.
  const dedupeFilename = (desired: string, existing: DriveItemRef[]): string => {
    const taken = new Set(existing.map((it) => (it.name || '').toLowerCase()));
    if (!taken.has(desired.toLowerCase())) return desired;
    const dot = desired.lastIndexOf('.');
    const base = dot > 0 ? desired.slice(0, dot) : desired;
    const ext = dot > 0 ? desired.slice(dot) : '';
    for (let n = 2; n < 1000; n++) {
      const candidate = `${base} (${n})${ext}`;
      if (!taken.has(candidate.toLowerCase())) return candidate;
    }
    return desired;
  };

  const attachPo = async () => {
    if (!project || !poFile) return;
    setPoBusy(true);
    setPoErr('');
    try {
      const token = await getOneDriveToken();
      if (!token) throw new Error('Not signed in to OneDrive.');
      const driveId = await resolveCorporateDriveId(token);

      let executionFolderId = project.executionFolderId;
      let executionFolderUrl = project.executionFolderUrl;
      if (!executionFolderId) {
        const ref = await ensureExecutionFolder(token, project);
        executionFolderId = ref.id;
        executionFolderUrl = ref.webUrl;
        await updateProject(project.id, { executionFolderId, executionFolderUrl });
      }

      const poFolder = await getOrCreateChildFolderById(token, driveId, executionFolderId, 'Customer PO');
      const existing = await listChildrenById(token, driveId, poFolder.id);
      const filename = dedupeFilename(poFile.name, existing);
      const uploaded = await uploadFileToFolderById(token, driveId, poFolder.id, filename, poFile);

      const entry: CustomerPO = {
        id: nanoid(),
        poNumber: poNumber.trim() || undefined,
        poDate: poDate || undefined,
        fileName: filename,
        driveItemId: uploaded.id,
        webUrl: uploaded.webUrl || undefined,
        fileSize: poFile.size,
        uploadedBy: (currentUser?.full_name?.trim() || currentUser?.username || '').trim() || undefined,
        uploadedAt: new Date().toISOString(),
      };
      const customerPOs = [...(project.customerPOs || []), entry];
      await updateProject(project.id, { customerPOs });
      setPoDialogOpen(false);
    } catch (e) {
      setPoErr(e instanceof Error ? e.message : 'Failed to attach PO');
    } finally {
      setPoBusy(false);
    }
  };

  const deletePo = async () => {
    if (!project || !poDeleteTarget) return;
    setPoDeleteBusy(true);
    try {
      const customerPOs = (project.customerPOs || []).filter((p) => p.id !== poDeleteTarget.id);
      await updateProject(project.id, { customerPOs });
      try {
        const token = await getOneDriveToken();
        if (token) {
          const driveId = await resolveCorporateDriveId(token);
          await deleteDriveItem(token, driveId, poDeleteTarget.driveItemId);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[OneDrive] failed to delete PO file, entry removed anyway', err);
      }
      setPoDeleteTarget(null);
    } finally {
      setPoDeleteBusy(false);
    }
  };

  // Upload arbitrary files straight into the project's active OneDrive folder
  // (proposal folder while the project is open; project/execution folder once
  // it's won). Creates the folder on the fly if it doesn't exist yet. Files land
  // at the folder root so the user can organise them from OneDrive afterwards.
  const uploadProjectFiles = async (fileList: FileList | File[] | null) => {
    if (!project) return;
    const MAX_BYTES = 4 * 1024 * 1024; // Graph simple-upload / function payload ceiling
    const all = Array.from(fileList || []);
    const oversized = all.filter((f) => f.size > MAX_BYTES).map((f) => f.name);
    const files = all.filter((f) => f.size <= MAX_BYTES);
    if (files.length === 0) {
      setProjectFilesErr(
        oversized.length
          ? `Too large (max 4 MB): ${oversized.join(', ')} — add these directly in OneDrive.`
          : '',
      );
      if (projectFilesInputRef.current) projectFilesInputRef.current.value = '';
      return;
    }
    setProjectFilesBusy(true);
    setProjectFilesErr('');
    setProjectFilesProgress({ done: 0, total: files.length });
    try {
      const token = await getOneDriveToken();
      if (!token) throw new Error('Not signed in to OneDrive.');
      const driveId = await resolveCorporateDriveId(token);

      const won = project.status === 'won';
      let folderId = won ? project.executionFolderId : project.proposalFolderId;
      if (!folderId) {
        const ref = won
          ? await ensureExecutionFolder(token, project)
          : await ensureProposalFolder(token, project);
        folderId = ref.id;
        await updateProject(
          project.id,
          won
            ? { executionFolderId: ref.id, executionFolderUrl: ref.webUrl }
            : { proposalFolderId: ref.id, proposalFolderUrl: ref.webUrl },
        );
      }

      const taken: DriveItemRef[] = await listChildrenById(token, driveId, folderId);
      const done: { name: string; url?: string }[] = [];
      const failed: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const name = dedupeFilename(file.name, taken);
        try {
          const up = await uploadFileToFolderById(token, driveId, folderId, name, file);
          taken.push({ id: up.id, name, webUrl: up.webUrl });
          done.push({ name, url: up.webUrl || undefined });
        } catch (err) {
          failed.push(file.name);
          // eslint-disable-next-line no-console
          console.warn('[OneDrive] project file upload failed', file.name, err);
        }
        setProjectFilesProgress({ done: i + 1, total: files.length });
      }
      if (done.length > 0) setUploadedProjectFiles((prev) => [...done, ...prev].slice(0, 50));
      // Refresh the folder browser if it's open at the root (where uploads land).
      if (done.length > 0 && fbOpen && fbStack.length === 1) void fbLoad(fbStack);
      const problems = [
        ...failed.map((n) => `${n} (upload failed)`),
        ...oversized.map((n) => `${n} (over 4 MB)`),
      ];
      if (problems.length) setProjectFilesErr(`Skipped: ${problems.join(', ')}`);
    } catch (e) {
      setProjectFilesErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setProjectFilesBusy(false);
      setProjectFilesProgress(null);
      if (projectFilesInputRef.current) projectFilesInputRef.current.value = '';
    }
  };

  const createProposalFolderManually = async () => {
    if (!project) return;
    setOneDriveBusy('proposal');
    setOneDriveErr('');
    setOneDriveInfo('');
    try {
      const token = await getOneDriveToken();
      if (!token) {
        setOneDriveErr('Not signed in to OneDrive.');
        return;
      }
      const ref = await ensureProposalFolder(token, project);
      await updateProject(project.id, {
        proposalFolderId: ref.id,
        proposalFolderUrl: ref.webUrl,
      });
      if (ref.matchedExisting) {
        setOneDriveInfo(`Linked to existing folder: "${ref.folderName}"`);
      }
    } catch (e) {
      setOneDriveErr(e instanceof Error ? e.message : 'Failed to create proposal folder');
    } finally {
      setOneDriveBusy(null);
    }
  };

  /**
   * Verify a stored OneDrive folder still exists, then either open it or, if it
   * was deleted out-of-band (via OneDrive web/Finder), clear the stale URL on
   * the project so the button reverts to "Create…". Best-effort: if the verify
   * itself fails (network/auth blip), fall through to opening the URL so we
   * don't block the user on a folder that's probably fine.
   */
  const openFolderOrSelfHeal = async (which: 'proposal' | 'execution') => {
    if (!project) return;
    const folderUrl = which === 'proposal' ? project.proposalFolderUrl : project.executionFolderUrl;
    const folderId = which === 'proposal' ? project.proposalFolderId : project.executionFolderId;
    if (!folderUrl) return;

    // No id to verify against — just open.
    if (!folderId) {
      window.open(folderUrl, '_blank', 'noopener');
      return;
    }

    try {
      const token = await getOneDriveToken();
      if (!token) {
        // Not signed in — just open and let OneDrive prompt for sign-in.
        window.open(folderUrl, '_blank', 'noopener');
        return;
      }
      const driveId = await resolveCorporateDriveId(token);
      const item = await verifyDriveItem(token, driveId, folderId);
      if (item) {
        // If the folder was moved (e.g. manually into a year subfolder), the
        // stored webUrl is stale. Refresh it silently so subsequent opens work.
        const freshUrl = item.webUrl || folderUrl;
        if (freshUrl && freshUrl !== folderUrl) {
          const urlPatch = which === 'proposal'
            ? { proposalFolderUrl: freshUrl }
            : { executionFolderUrl: freshUrl };
          updateProject(project.id, urlPatch).catch(() => {});
        }
        window.open(freshUrl || folderUrl, '_blank', 'noopener');
        return;
      }
      // Self-heal: folder was deleted in OneDrive. Clear stored URL/id so the
      // button reverts to "Create proposal/execution folder".
      //
      // Special case: after a project is promoted (status → 'won'), the move
      // preserves the drive item's id, so proposalFolderId === executionFolderId
      // and both refs point at the same physical folder. If that single folder
      // is deleted, clearing only one side leaves the other side stale — which
      // makes the button mislabel itself "Promote to execution" and then the
      // promote operation 404s on the non-existent source. So when the two refs
      // share an id, clear both atomically.
      const sharedId =
        project.proposalFolderId &&
        project.executionFolderId &&
        project.proposalFolderId === project.executionFolderId;
      const patch = sharedId
        ? { proposalFolderId: '', proposalFolderUrl: '', executionFolderId: '', executionFolderUrl: '' }
        : which === 'proposal'
          ? { proposalFolderId: '', proposalFolderUrl: '' }
          : { executionFolderId: '', executionFolderUrl: '' };
      await updateProject(project.id, patch);
      setOneDriveErr(`The ${which} folder no longer exists in OneDrive. Click "Create ${which} folder" to make a new one.`);
    } catch (err) {
      // Verify failed for some non-404 reason — open the link anyway so the
      // user isn't blocked. They'll see the OneDrive error directly if it's broken.
      // eslint-disable-next-line no-console
      console.warn(`[OneDrive] verify ${which} folder failed, opening anyway`, err);
      window.open(folderUrl, '_blank', 'noopener');
    }
  };

  const openFolderInApp = (folderUrl: string) => {
    const url = `odopen://sync?userEmail=${encodeURIComponent(onedriveConfig.driveOwner)}&weUrl=${encodeURIComponent(folderUrl)}`;
    window.location.href = url;
  };

  // "Link existing folder" — the dialog auto-scans OneDrive for folders whose
  // name starts with this project's PCS code and lists them as one-click
  // suggestions. Falls back to manual URL paste at the bottom for edge cases
  // (folders renamed beyond recognition, missing PCS code prefix, etc.).
  const [linkDialogOpen, setLinkDialogOpen] = useState<null | 'proposal' | 'execution'>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkErr, setLinkErr] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSuggestions, setLinkSuggestions] = useState<DriveItemRef[]>([]);
  const [linkSuggestionsLoading, setLinkSuggestionsLoading] = useState(false);

  const fetchSuggestions = async (kind: 'proposal' | 'execution') => {
    if (!project) return;
    setLinkSuggestionsLoading(true);
    setLinkSuggestions([]);
    try {
      const token = await getOneDriveToken();
      if (!token) {
        setLinkErr('Not signed in to OneDrive — can\'t fetch suggestions. Paste a URL below instead.');
        return;
      }
      const driveId = await resolveCorporateDriveId(token);
      const root = kind === 'proposal' ? onedriveConfig.proposalRoot : onedriveConfig.executionRoot;
      const year = String(new Date().getFullYear());
      const yearRoot = root ? `${root}/${year}` : year;
      const prefix = projectCodePrefix(project);
      // Scan both flat root (historical) and current year subfolder in parallel
      const [flat, yeared] = await Promise.allSettled([
        listFoldersWithPrefix(token, driveId, root, prefix),
        listFoldersWithPrefix(token, driveId, yearRoot, prefix),
      ]);
      const matches = [
        ...(flat.status === 'fulfilled' ? flat.value : []),
        ...(yeared.status === 'fulfilled' ? yeared.value : []),
      ];
      setLinkSuggestions(matches);
    } catch (e) {
      // Don't set the error red — fall back to URL input silently with a hint.
      // eslint-disable-next-line no-console
      console.warn('[OneDrive] suggestion fetch failed', e);
    } finally {
      setLinkSuggestionsLoading(false);
    }
  };

  const openLinkDialog = (kind: 'proposal' | 'execution') => {
    setLinkUrl('');
    setLinkErr('');
    setLinkSuggestions([]);
    setLinkDialogOpen(kind);
    void fetchSuggestions(kind);
  };

  const linkToSuggestion = async (ref: DriveItemRef) => {
    if (!project || !linkDialogOpen) return;
    setLinkBusy(true);
    setLinkErr('');
    try {
      const patch = linkDialogOpen === 'proposal'
        ? { proposalFolderId: ref.id, proposalFolderUrl: ref.webUrl }
        : { executionFolderId: ref.id, executionFolderUrl: ref.webUrl };
      await updateProject(project.id, patch);
      if (linkDialogOpen === 'execution' && project.mainProjectId) {
        await fetch(`/api/projects/${project.mainProjectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ executionFolderId: ref.id, executionFolderUrl: ref.webUrl }),
        }).catch(() => {});
      }
      setLinkDialogOpen(null);
    } catch (e) {
      setLinkErr(e instanceof Error ? e.message : 'Failed to link folder');
    } finally {
      setLinkBusy(false);
    }
  };

  const submitLinkDialog = async () => {
    if (!project || !linkDialogOpen) return;
    setLinkErr('');
    setLinkBusy(true);
    try {
      const token = await getOneDriveToken();
      if (!token) {
        setLinkErr('Not signed in to OneDrive.');
        return;
      }
      const ref = await resolveSharingUrl(token, linkUrl);
      if (!ref.isFolder) {
        setLinkErr('That URL points to a file, not a folder. Paste a folder URL.');
        return;
      }
      const patch = linkDialogOpen === 'proposal'
        ? { proposalFolderId: ref.id, proposalFolderUrl: ref.webUrl }
        : { executionFolderId: ref.id, executionFolderUrl: ref.webUrl };
      await updateProject(project.id, patch);
      if (linkDialogOpen === 'execution' && project.mainProjectId) {
        await fetch(`/api/projects/${project.mainProjectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ executionFolderId: ref.id, executionFolderUrl: ref.webUrl }),
        }).catch(() => {});
      }
      setLinkDialogOpen(null);
    } catch (e) {
      setLinkErr(e instanceof Error ? e.message : 'Failed to resolve URL');
    } finally {
      setLinkBusy(false);
    }
  };

  const createExecutionFolderManually = async () => {
    if (!project) return;
    setOneDriveBusy('execution');
    setOneDriveErr('');
    setOneDriveInfo('');
    try {
      const token = await getOneDriveToken();
      if (!token) {
        setOneDriveErr('Not signed in to OneDrive.');
        return;
      }
      // Uses IOCT naming when mainProjectNo is set:
      // `IOCT2605001-LBI RCS Plaridel Troubleshooting`. Renames an existing
      // PCS-named execution folder if needed; otherwise promotes/creates.
      const { executionFolder, proposalFolder } = await ensureWonExecutionLayout(token, project);
      await updateProject(project.id, {
        ...(proposalFolder?.webUrl ? { proposalFolderUrl: proposalFolder.webUrl } : {}),
        executionFolderId: executionFolder.id,
        executionFolderUrl: executionFolder.webUrl,
      });
      if (project.mainProjectId) {
        await fetch(`/api/projects/${project.mainProjectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            executionFolderId: executionFolder.id,
            executionFolderUrl: executionFolder.webUrl,
          }),
        }).catch(() => {});
      }
      if (executionFolder.name) {
        setOneDriveInfo(`Execution folder: "${executionFolder.name}"`);
      }
    } catch (e) {
      setOneDriveErr(e instanceof Error ? e.message : 'Failed to create execution folder');
    } finally {
      setOneDriveBusy(null);
    }
  };

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<QuotationKind>('IOCT');
  const [recipientId, setRecipientId] = useState('');
  // Locked in at creation, same as `kind` — see QuotationScopeCategory.
  const [scopeCategory, setScopeCategory] = useState<QuotationScopeCategory>('both');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ code: '', name: '', location: '', customerId: '', partnerId: '' });

  // Legacy-quotation single-file import (for adding a missing kind to an existing project)
  const legacyFileInputRef = useRef<HTMLInputElement | null>(null);
  // Page-wide drag-and-drop state (auto-routes by file extension)
  const [dragOver, setDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [legacyParsed, setLegacyParsed] = useState<ParsedProject | null>(null);
  const [legacySource, setLegacySource] = useState<string>('');
  const [legacySelect, setLegacySelect] = useState<{ IOCT: boolean; ACTI: boolean }>({ IOCT: false, ACTI: false });
  const [legacyRevisions, setLegacyRevisions] = useState<{ IOCT: string; ACTI: string }>({ IOCT: '00', ACTI: '00' });
  const [legacyRecipient, setLegacyRecipient] = useState<{ IOCT: string; ACTI: string }>({ IOCT: '', ACTI: '' });
  // Per-kind VAT inclusion: true → use the VAT-IN grand total from the workbook
  // (default — what was actually issued); false → drop VAT and use the VAT-EX
  // subtotal as the grand total. Lets the user record a VAT-EX quotation when
  // the workbook was authored VAT-IN (or vice-versa) without editing the file.
  const [legacyIncludeVat, setLegacyIncludeVat] = useState<{ IOCT: boolean; ACTI: boolean }>({ IOCT: true, ACTI: true });
  const [legacyError, setLegacyError] = useState<string>('');
  const [legacyImporting, setLegacyImporting] = useState(false);

  // Legacy-quotation PDF import (when the source .xlsx is missing/corrupted)
  const legacyPdfInputRef = useRef<HTMLInputElement | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfParsed, setPdfParsed] = useState<ParsedLegacyPdf | null>(null);
  const [pdfForm, setPdfForm] = useState({
    kind: 'ACTI' as QuotationKind,
    revision: '00',
    recipientId: '',
    date: '',
    validityDays: 30,
    warrantyMonths: 12,
    paymentTerms: '',
    deliveryTerms: '',
    preparedBy: '',
    authorizedBy: 'Renzel Punongbayan',
    sectionA: 0,
    sectionB: 0,
    sectionC: 0,
    vatMode: 'VAT-EX' as 'VAT-EX' | 'VAT-IN',
    grandTotal: 0,
  });
  const [pdfError, setPdfError] = useState<string>('');
  const [pdfImporting, setPdfImporting] = useState(false);

  if (!project) return <Typography>Project not found. <Link to="/sales/calcsheet/projects">Back</Link></Typography>;

  const customer = clients.find((c) => c.id === project.customerId);
  const partner = clients.find((c) => c.id === project.partnerId);

  const startNew = (k: QuotationKind) => {
    setKind(k);
    setRecipientId(k === 'IOCT' ? (partner?.id ?? customer?.id ?? '') : (customer?.id ?? ''));
    setScopeCategory('both');
    setOpen(true);
  };
  const create = async () => {
    const q = await createQuotation(project.id, kind, recipientId || null, scopeCategory);
    setOpen(false);
    navigate(`/sales/calcsheet/quotations/${q.id}`);
  };

  const hasIoct = quotations.some((q) => q.kind === 'IOCT');
  const hasActi = quotations.some((q) => q.kind === 'ACTI');

  // ── In-app OneDrive folder browser ───────────────────────────────────────
  // Root is the project's active folder: proposal folder while quoting, the
  // project/execution folder once won. Read-only — files open in OneDrive.
  const activeRootFolderId = project.status === 'won' ? project.executionFolderId : project.proposalFolderId;
  const activeRootFolderLabel = project.status === 'won' ? 'Project folder' : 'Proposal folder';

  const fbLoad = async (stack: { id: string; name: string }[]) => {
    const target = stack[stack.length - 1];
    if (!target) return;
    setFbLoading(true);
    setFbErr('');
    try {
      const token = await getOneDriveToken();
      if (!token) throw new Error('Not signed in to OneDrive.');
      const driveId = await resolveCorporateDriveId(token);
      const items = await listChildrenById(token, driveId, target.id);
      items.sort((a, b) => {
        if (!!a.isFolder !== !!b.isFolder) return a.isFolder ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setFbItems(items);
      setFbStack(stack);
    } catch (e) {
      setFbErr(e instanceof Error ? e.message : 'Failed to load folder');
    } finally {
      setFbLoading(false);
    }
  };

  const openFileBrowser = () => {
    if (!activeRootFolderId) return;
    setFbOpen(true);
    void fbLoad([{ id: activeRootFolderId, name: activeRootFolderLabel }]);
  };
  const fbEnter = (item: DriveItemRef) => {
    if (!item.isFolder || fbLoading) return;
    void fbLoad([...fbStack, { id: item.id, name: item.name || '…' }]);
  };
  const fbGoTo = (index: number) => {
    if (fbLoading || index === fbStack.length - 1) return;
    void fbLoad(fbStack.slice(0, index + 1));
  };
  const fbRefresh = () => {
    if (!fbLoading && fbStack.length) void fbLoad(fbStack);
  };

  const openEdit = () => {
    setEditForm({
      code: project.code,
      name: project.name,
      location: project.location ?? '',
      customerId: project.customerId ?? '',
      partnerId: project.partnerId ?? '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    await updateProject(project.id, {
      code: editForm.code.trim(),
      name: editForm.name.trim(),
      location: editForm.location.trim() || undefined,
      customerId: editForm.customerId || null,
      partnerId: editForm.partnerId || null,
    });
    setEditOpen(false);
  };

  // ── Legacy-quotation import (single .xlsx into THIS project) ──────────────
  const nextRevisionFor = (k: QuotationKind, parsedRev: string): string => {
    const existing = quotations.filter((q) => q.kind === k).map((q) => q.revision);
    if (!existing.includes(parsedRev)) return parsedRev;
    // bump until free, padding to 2 digits
    let n = parseInt(parsedRev, 10);
    if (!Number.isFinite(n)) n = 0;
    while (existing.includes(String(n).padStart(2, '0'))) n += 1;
    return String(n).padStart(2, '0');
  };

  const onLegacyFilePicked = async (file: File | null) => {
    if (!file) return;
    setLegacyError('');
    try {
      const buf = await file.arrayBuffer();
      const parsed = parseLegacyWorkbook(buf, {
        filename: file.name,
        offerPdfs: [],
        projectFolder: project.code,
      });
      const hasIoct = parsed.quotations.some((q) => q.kind === 'IOCT');
      const hasActi = parsed.quotations.some((q) => q.kind === 'ACTI');
      const ioctParsed = parsed.quotations.find((q) => q.kind === 'IOCT');
      const actiParsed = parsed.quotations.find((q) => q.kind === 'ACTI');
      setLegacyParsed(parsed);
      setLegacySource(file.name);
      setLegacySelect({ IOCT: hasIoct, ACTI: hasActi });
      setLegacyRevisions({
        IOCT: nextRevisionFor('IOCT', ioctParsed?.revision || '00'),
        ACTI: nextRevisionFor('ACTI', actiParsed?.revision || '00'),
      });
      // Default recipients: IOCT → partner (when set) else customer; ACTI → customer
      setLegacyRecipient({
        IOCT: project.partnerId ?? project.customerId ?? '',
        ACTI: project.customerId ?? '',
      });
      // Default VAT inclusion: keep VAT only when the workbook actually has a
      // non-zero VAT amount for that kind. Otherwise default to VAT-EX so the
      // user doesn't have to flip a switch on every import.
      setLegacyIncludeVat({
        IOCT: (ioctParsed?.legacyTotalsSnapshot.vat ?? 0) > 0,
        ACTI: (actiParsed?.legacyTotalsSnapshot.vat ?? 0) > 0,
      });
      setLegacyOpen(true);
    } catch (err: any) {
      setLegacyError(`Parse failed: ${err.message || err}`);
      setLegacyOpen(true);
    } finally {
      // reset so the same file can be re-picked after a failure
      if (legacyFileInputRef.current) legacyFileInputRef.current.value = '';
    }
  };

  const closeLegacyDialog = () => {
    setLegacyOpen(false);
    setLegacyParsed(null);
    setLegacySource('');
    setLegacyError('');
    setLegacyImporting(false);
  };

  const importLegacySelected = async () => {
    if (!legacyParsed) return;
    setLegacyImporting(true);
    setLegacyError('');
    try {
      const wantKinds: QuotationKind[] = [];
      if (legacySelect.IOCT) wantKinds.push('IOCT');
      if (legacySelect.ACTI) wantKinds.push('ACTI');
      const picks: ParsedQuotation[] = legacyParsed.quotations.filter((q) => wantKinds.includes(q.kind));
      if (picks.length === 0) {
        setLegacyError('Pick at least one kind to import.');
        setLegacyImporting(false);
        return;
      }
      for (const pq of picks) {
        const revision = legacyRevisions[pq.kind];
        // Guard against duplicate (kind, revision) on this project
        const dup = quotations.find((q) => q.kind === pq.kind && q.revision === revision);
        if (dup) {
          setLegacyError(`A ${pq.kind} rev ${revision} already exists on this project. Change the revision in the dialog and try again.`);
          setLegacyImporting(false);
          return;
        }
        const importedFrom: { sourceFile: string; importedAt: string; originalCode?: string } = {
          sourceFile: legacySource,
          importedAt: new Date().toISOString(),
          originalCode: legacyParsed.originalCode,
        };
        // Honor the per-kind "Include VAT" toggle: when off, zero out the VAT
        // amount and recompute the grand total as (subtotal − discount). The
        // snapshot is the source of truth for legacy quotations, so this is
        // where the decision is baked in.
        const includeVat = legacyIncludeVat[pq.kind];
        const snap = pq.legacyTotalsSnapshot;
        const snapshot = includeVat ? snap : {
          ...snap,
          vat: 0,
          grandTotal: Math.max(0, snap.subtotal - snap.discount),
        };
        const built: Quotation = {
          id: nanoid(8),
          projectId: project.id,
          kind: pq.kind,
          revision,
          recipientId: legacyRecipient[pq.kind] || null,
          validityDays: pq.validityDays,
          paymentTerms: pq.paymentTerms,
          deliveryTerms: pq.deliveryTerms,
          warrantyMonths: pq.warrantyMonths,
          productMarkupPct: pq.productMarkupPct,
          laborMarkupPct: pq.laborMarkupPct,
          generalReqMarkupPct: pq.generalReqMarkupPct,
          globalContingencyPct: pq.globalContingencyPct,
          discountPct: pq.discountPct,
          vatPct: includeVat ? pq.vatPct : 0,
          generalReqts: pq.generalReqts,
          components: pq.components,
          services: pq.services,
          manpower: pq.manpower,
          servicesFromManpower: pq.servicesFromManpower,
          preparedBy: pq.preparedBy,
          authorizedBy: pq.authorizedBy,
          formulaVersion: 'legacy',
          legacyTotalsSnapshot: snapshot,
          importedFrom,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        await importQuotation(built);
      }
      closeLegacyDialog();
    } catch (err: any) {
      setLegacyError(err.message || String(err));
      setLegacyImporting(false);
    }
  };

  // Route a picked/dropped file to the right importer based on extension.
  const handleLegacyFile = (file: File | null) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (/\.xlsx?$/.test(lower)) {
      onLegacyFilePicked(file);
    } else if (/\.pdf$/.test(lower)) {
      onLegacyPdfPicked(file);
    } else {
      setLegacyError(`Unsupported file type: ${file.name}. Drop a .xlsx or .pdf.`);
      setLegacyOpen(true);
    }
  };

  // Drag-and-drop handlers (page-wide overlay)
  const onDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types?.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types?.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer.types?.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleLegacyFile(f);
  };

  // ── Legacy-quotation PDF import ───────────────────────────────────────────
  const onLegacyPdfPicked = async (file: File | null) => {
    if (!file) return;
    setPdfError('');
    setPdfParsing(true);
    setPdfOpen(true);
    try {
      const parsed = await parseLegacyPdf(file);
      const inferredKind: QuotationKind = parsed.kind === 'IOCT' ? 'IOCT' : 'ACTI';
      const initialRev = nextRevisionFor(inferredKind, parsed.revision || '00');
      // Guess recipient by matching name against existing clients (loose)
      const lcName = parsed.recipientName.toLowerCase();
      const matched = clients.find((c) => lcName && c.name.toLowerCase().includes(lcName.split(' ')[0]));
      setPdfParsed(parsed);
      setPdfForm({
        kind: inferredKind,
        revision: initialRev,
        recipientId:
          matched?.id ??
          (inferredKind === 'IOCT' ? (project.partnerId ?? project.customerId ?? '') : (project.customerId ?? '')),
        date: parsed.date || project.date || '',
        validityDays: parsed.validityDays,
        warrantyMonths: parsed.warrantyMonths,
        paymentTerms: parsed.paymentTerms || '30% DP, 70% Progress Billing',
        deliveryTerms: parsed.deliveryTerms || '',
        preparedBy: '',
        authorizedBy: parsed.authorizedBy || 'Renzel Punongbayan',
        sectionA: parsed.sectionA,
        sectionB: parsed.sectionB,
        sectionC: parsed.sectionC,
        vatMode: parsed.vatMode === 'VAT-IN' ? 'VAT-IN' : 'VAT-EX',
        grandTotal: parsed.grandTotal,
      });
    } catch (err: any) {
      setPdfError(`Parse failed: ${err.message || err}`);
    } finally {
      setPdfParsing(false);
      if (legacyPdfInputRef.current) legacyPdfInputRef.current.value = '';
    }
  };

  const closePdfDialog = () => {
    setPdfOpen(false);
    setPdfParsed(null);
    setPdfError('');
    setPdfImporting(false);
  };

  const importLegacyPdf = async () => {
    if (!pdfParsed) return;
    // Compute snapshot from form
    const sub = pdfForm.vatMode === 'VAT-IN' ? +(pdfForm.grandTotal / 1.12).toFixed(2) : pdfForm.grandTotal;
    const v = pdfForm.vatMode === 'VAT-IN' ? +(pdfForm.grandTotal - sub).toFixed(2) : 0;
    if (!pdfForm.grandTotal || pdfForm.grandTotal <= 0) {
      setPdfError('Grand total is required and must be greater than zero.');
      return;
    }
    const dup = quotations.find((q) => q.kind === pdfForm.kind && q.revision === pdfForm.revision);
    if (dup) {
      setPdfError(`A ${pdfForm.kind} rev ${pdfForm.revision} already exists on this project. Change the revision.`);
      return;
    }
    setPdfImporting(true);
    setPdfError('');
    try {
      const snapshot = {
        generalReqtsCost: pdfForm.sectionA,
        generalReqtsWithContingency: pdfForm.sectionA,
        generalReqtsSubtotal: pdfForm.sectionA,
        componentsCost: pdfForm.sectionB,
        componentsSubtotal: pdfForm.sectionB,
        laborCost: pdfForm.sectionC,
        laborWithContingency: pdfForm.sectionC,
        servicesSubtotal: pdfForm.sectionC,
        subtotal: sub,
        discount: 0,
        vat: v,
        grandTotal: pdfForm.grandTotal,
      };
      const built: Quotation = {
        id: nanoid(8),
        projectId: project.id,
        kind: pdfForm.kind,
        revision: pdfForm.revision,
        recipientId: pdfForm.recipientId || null,
        validityDays: pdfForm.validityDays,
        paymentTerms: pdfForm.paymentTerms,
        deliveryTerms: pdfForm.deliveryTerms,
        warrantyMonths: pdfForm.warrantyMonths,
        productMarkupPct: 0,
        laborMarkupPct: 0,
        generalReqMarkupPct: 0,
        globalContingencyPct: 0,
        discountPct: 0,
        vatPct: pdfForm.vatMode === 'VAT-IN' ? 12 : 0,
        generalReqts: [],
        components: [],
        services: [],
        manpower: [],
        servicesFromManpower: true,
        preparedBy: pdfForm.preparedBy,
        authorizedBy: pdfForm.authorizedBy,
        formulaVersion: 'legacy',
        legacyTotalsSnapshot: snapshot,
        importedFrom: {
          sourceFile: pdfParsed.sourceFile,
          importedAt: new Date().toISOString(),
          originalCode: pdfParsed.refCode,
          pdfFilename: pdfParsed.sourceFile,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await importQuotation(built);
      closePdfDialog();
    } catch (err: any) {
      setPdfError(err.message || String(err));
      setPdfImporting(false);
    }
  };

  return (
    <Box
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{ position: 'relative', minHeight: '100%' }}
    >
      {dragOver && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            bgcolor: 'rgba(237, 108, 2, 0.08)',
            border: '4px dashed',
            borderColor: 'warning.main',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper elevation={6} sx={{ p: 4, textAlign: 'center', maxWidth: 420 }}>
            <UploadFileIcon sx={{ fontSize: 56, color: 'warning.main', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Drop to import as legacy</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Accepts <strong>.xlsx</strong> (full parse) or <strong>.pdf</strong> (snapshot totals).
              The file type is auto-detected.
            </Typography>
          </Paper>
        </Box>
      )}
    <Stack spacing={3}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0, flex: '1 1 260px' }}>
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', ...(project.code ? {} : { fontStyle: 'italic' }) }}>
              {project.code || 'No code yet — assign a client to generate one'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{project.name}</Typography>
            {project.location && (
              <Typography color="text.secondary" sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: { xs: 2, sm: 'unset' }, overflow: 'hidden' }} title={project.location}>
                {project.location}
              </Typography>
            )}
          </Stack>
          <IconButton size="small" onClick={openEdit} title="Edit project details" sx={{ mt: 0.5 }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Button component={Link} to="/sales/calcsheet/projects" variant="text" size="small">← All projects</Button>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Box sx={{
          display: { xs: 'grid', sm: 'flex' }, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          flexWrap: 'wrap', alignItems: { sm: 'center' }, columnGap: { xs: 2, sm: 4 }, rowGap: 1.5,
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Customer</Typography>
            <Typography variant="body2">{customer?.name ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Partner</Typography>
            <Typography variant="body2">{partner?.name ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box>
              <TextField
                select
                size="small"
                variant="standard"
                value={project.status}
                onChange={(e) => { void changeStatus(e.target.value as ProjectStatus); }}
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem' } }}
                sx={{ minWidth: 80 }}
              >
                {PROJECT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{projectStatusLabel(s)}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Grade</Typography>
            <Box>
              <TextField
                select
                size="small"
                variant="standard"
                value={project.opportunityGrade ?? ''}
                onChange={(e) => updateProject(project.id, { opportunityGrade: (e.target.value || undefined) as OpportunityGrade | undefined })}
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem' } }}
                sx={{ minWidth: 110 }}
              >
                <MenuItem value=""><em>Ungraded</em></MenuItem>
                {OPPORTUNITY_GRADES.map((g) => (
                  <MenuItem key={g} value={g}>{opportunityGradeLabel(g)}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          {project.mainProjectId && (
            <Box>
              <Typography variant="caption" color="text.secondary">Project List status</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={project.mainProjectStatus || 'Not Started'}
                  color={
                    String(project.mainProjectStatus || '').toLowerCase().includes('complete') ||
                    String(project.mainProjectStatus || '').toLowerCase().includes('closed')
                      ? 'success'
                      : 'default'
                  }
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {project.mainProjectProgressPercent ?? 0}%
                </Typography>
              </Stack>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Created by</Typography>
            <Typography variant="body2">{project.createdByName ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Date</Typography>
            <Box>
              <TextField
                type="date"
                size="small"
                variant="standard"
                value={project.date?.slice(0, 10) || ''}
                onChange={(e) => updateProject(project.id, { date: e.target.value })}
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem' } }}
                sx={{ width: 150 }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
        {project.mainProjectId ? (
          <>
            <Button
              component={Link}
              to="/dashboard"
              variant="outlined"
              size="small"
              color="success"
              startIcon={<LinkIcon />}
            >
              Linked to Project List
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={mainSyncBusy}
              onClick={() => { void syncToMainProject(false); }}
              title={project.awarded
                ? 'Push the locked awarded price to the Project List contract amount. Does not reset status or billing.'
                : 'Push latest Sales (IOCT) quotation total to Project List contract amount. Does not reset status or billing.'}
            >
              {mainSyncBusy ? 'Syncing...' : project.awarded ? 'Sync locked price' : 'Sync contract from Sales'}
            </Button>
          </>
        ) : project.status === 'won' ? (
          <>
            <Button
              variant="contained"
              size="small"
              color="success"
              disabled={mainSyncBusy}
              startIcon={<LinkIcon />}
              onClick={() => { void syncToMainProject(false); }}
            >
              {mainSyncBusy ? 'Creating...' : 'Create Project List record'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => openLinkExistingDialog(false)}
            >
              Link to existing
            </Button>
          </>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<LinkIcon />}
            onClick={() => openLinkExistingDialog(false)}
          >
            Link to existing project
          </Button>
        )}
        {mainSyncErr && (
          <Typography variant="caption" color="error.main">
            {mainSyncErr}
          </Typography>
        )}
        {mainSyncInfo && !mainSyncErr && (
          <Typography variant="caption" color="success.main">
            {mainSyncInfo}
          </Typography>
        )}
      </Stack>

      {(project.status === 'won' || project.awarded) && (
        <AwardedBudgetCard
          project={project}
          quotations={quotations}
          isAdmin={currentUser?.role === 'superadmin' || currentUser?.role === 'admin'}
          busy={awardBusy || mainSyncBusy}
          refreshKey={awardRefresh}
          onLock={() => {
            setAwardPickId(defaultAwardQuotationId(project, quotations));
            setLockDialogOpen(true);
          }}
          onUnlock={() => setUnlockConfirmOpen(true)}
          onRepush={() => { if (project.awarded) void lockAward(project.awarded.quotationId); }}
        />
      )}

      {isCorporateOneDriveConfigured() && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
          {!oneDriveSignedIn ? (
            <Button
              variant="outlined"
              size="small"
              color="info"
              startIcon={<CloudOffIcon />}
              onClick={() => { void oneDriveLogin(); }}
            >
              Sign in to OneDrive
            </Button>
          ) : (
            <>
              {project.status !== 'won' && (
                project.proposalFolderUrl ? (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FolderIcon />}
                      endIcon={<OpenInNewIcon />}
                      onClick={() => { void openFolderOrSelfHeal('proposal'); }}
                    >
                      Proposal folder
                    </Button>
                    <Tooltip title="Open in OneDrive desktop app">
                      <IconButton size="small" onClick={() => openFolderInApp(project.proposalFolderUrl!)}>
                        <DesktopMacIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      color="info"
                      startIcon={<CloudIcon />}
                      disabled={oneDriveBusy === 'proposal'}
                      onClick={createProposalFolderManually}
                    >
                      {oneDriveBusy === 'proposal' ? 'Creating…' : 'Create proposal folder'}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={() => openLinkDialog('proposal')}
                    >
                      Link existing
                    </Button>
                  </>
                )
              )}
              {project.status === 'won' && (
                project.executionFolderUrl ? (
                  <>
                    {/* Project folder = the ops-named parent (IOCT2605001-LBI …) */}
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<FolderIcon />}
                      endIcon={<OpenInNewIcon />}
                      onClick={() => { void openFolderOrSelfHeal('execution'); }}
                    >
                      Project folder
                    </Button>
                    <Tooltip title="Open in OneDrive desktop app">
                      <IconButton size="small" onClick={() => openFolderInApp(project.executionFolderUrl!)}>
                        <DesktopMacIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* Proposal docs = the PCS subfolder inside the project folder.
                        Only shown when it has its own distinct URL (new structure).
                        If they're the same (old records), skip to avoid duplicates. */}
                    {project.proposalFolderUrl && project.proposalFolderUrl !== project.executionFolderUrl && (
                      <>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<FolderIcon />}
                          endIcon={<OpenInNewIcon />}
                          onClick={() => { void openFolderOrSelfHeal('proposal'); }}
                        >
                          Proposal docs
                        </Button>
                        <Tooltip title="Open in OneDrive desktop app">
                          <IconButton size="small" onClick={() => openFolderInApp(project.proposalFolderUrl!)}>
                            <DesktopMacIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Button
                      variant="text"
                      size="small"
                      color="success"
                      startIcon={<LinkIcon />}
                      onClick={() => openLinkDialog('execution')}
                    >
                      Re-link
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<CloudIcon />}
                      disabled={oneDriveBusy === 'execution'}
                      onClick={createExecutionFolderManually}
                    >
                      {oneDriveBusy === 'execution'
                        ? (project.proposalFolderId ? 'Moving…' : 'Creating…')
                        : (project.proposalFolderId ? 'Promote to execution' : 'Create execution folder')}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="success"
                      startIcon={<LinkIcon />}
                      onClick={() => openLinkDialog('execution')}
                    >
                      Link existing
                    </Button>
                  </>
                )
              )}
            </>
          )}
          {oneDriveErr && (
            <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
              {oneDriveErr}
            </Typography>
          )}
          {oneDriveInfo && !oneDriveErr && (
            <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
              {oneDriveInfo}
            </Typography>
          )}
        </Stack>
      )}

      {project.status === 'won' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Customer PO</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              disabled={!isCorporateOneDriveConfigured()}
              onClick={openPoDialog}
            >
              Attach PO
            </Button>
          </Stack>
          {!isCorporateOneDriveConfigured() && (
            <Typography variant="caption" color="text.secondary">
              OneDrive is not configured — PO attachments are unavailable.
            </Typography>
          )}
          {isCorporateOneDriveConfigured() && (project.customerPOs || []).length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No PO attached yet.
            </Typography>
          )}
          {(project.customerPOs || []).length > 0 && (
            <Stack spacing={0.5}>
              {project.customerPOs!.map((po) => (
                <Stack key={po.id} direction="row" alignItems="center" spacing={1}>
                  <DescriptionIcon fontSize="small" color="action" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      component={po.webUrl ? 'a' : 'span'}
                      href={po.webUrl || undefined}
                      target={po.webUrl ? '_blank' : undefined}
                      rel={po.webUrl ? 'noopener' : undefined}
                      sx={{ fontWeight: 500, textDecoration: po.webUrl ? 'underline' : 'none', color: po.webUrl ? 'primary.main' : 'text.primary' }}
                    >
                      {po.fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {[
                        po.poNumber ? `PO ${po.poNumber}` : null,
                        po.poDate || null,
                        po.uploadedBy || null,
                      ].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setPoDeleteTarget(po)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {isCorporateOneDriveConfigured() && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            transition: 'border-color .15s, background-color .15s',
            ...(projectFilesDragOver && oneDriveSignedIn
              ? { borderColor: 'primary.main', bgcolor: 'action.hover' }
              : null),
          }}
          onDragEnter={(e) => {
            if (!oneDriveSignedIn || !e.dataTransfer.types?.includes('Files')) return;
            e.preventDefault(); e.stopPropagation(); setProjectFilesDragOver(true);
          }}
          onDragOver={(e) => {
            if (!oneDriveSignedIn || !e.dataTransfer.types?.includes('Files')) return;
            e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy';
          }}
          onDragLeave={(e) => {
            if (!e.dataTransfer.types?.includes('Files')) return;
            e.preventDefault(); e.stopPropagation(); setProjectFilesDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault(); e.stopPropagation(); setProjectFilesDragOver(false);
            if (!oneDriveSignedIn || projectFilesBusy) return;
            void uploadProjectFiles(e.dataTransfer.files);
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Project files</Typography>
              <Typography variant="caption" color="text.secondary">
                Uploads straight into the {project.status === 'won' ? 'project' : 'proposal'} folder on OneDrive.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              disabled={!oneDriveSignedIn || projectFilesBusy}
              onClick={() => projectFilesInputRef.current?.click()}
            >
              {projectFilesBusy ? 'Uploading…' : 'Upload files'}
            </Button>
            <input
              ref={projectFilesInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => { void uploadProjectFiles(e.target.files); }}
            />
          </Stack>

          {!oneDriveSignedIn ? (
            <Typography variant="caption" color="text.secondary">
              Sign in to OneDrive above to upload files.
            </Typography>
          ) : projectFilesProgress ? (
            <Box sx={{ my: 1 }}>
              <LinearProgress
                variant="determinate"
                value={(projectFilesProgress.done / Math.max(1, projectFilesProgress.total)) * 100}
              />
              <Typography variant="caption" color="text.secondary">
                Uploading {projectFilesProgress.done} / {projectFilesProgress.total}…
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Drag &amp; drop files here, or use the button. Max 4&nbsp;MB per file — add larger files directly in OneDrive.
            </Typography>
          )}

          {projectFilesErr && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
              {projectFilesErr}
            </Typography>
          )}

          {uploadedProjectFiles.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="caption" color="success.main">Uploaded this session:</Typography>
              {uploadedProjectFiles.map((f, i) => (
                <Stack key={`${f.name}-${i}`} direction="row" alignItems="center" spacing={1}>
                  <DescriptionIcon fontSize="small" color="action" />
                  <Typography
                    variant="body2"
                    component={f.url ? 'a' : 'span'}
                    href={f.url || undefined}
                    target={f.url ? '_blank' : undefined}
                    rel={f.url ? 'noopener' : undefined}
                    sx={{ textDecoration: f.url ? 'underline' : 'none', color: f.url ? 'primary.main' : 'text.primary' }}
                  >
                    {f.name}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}

          {oneDriveSignedIn && (
            activeRootFolderId ? (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" flexWrap="wrap" useFlexGap alignItems="center" spacing={0.5}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<FolderIcon />}
                    onClick={() => (fbOpen ? setFbOpen(false) : openFileBrowser())}
                  >
                    {fbOpen ? 'Hide folder contents' : 'Browse folder contents'}
                  </Button>
                  {fbOpen && (
                    <Tooltip title="Refresh">
                      <span>
                        <IconButton size="small" onClick={fbRefresh} disabled={fbLoading}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>

                {fbOpen && (
                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                    <Stack direction="row" spacing={0.25} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.25, mb: 0.5 }}>
                      {fbStack.map((crumb, i) => (
                        <Box key={crumb.id} sx={{ display: 'flex', alignItems: 'center' }}>
                          {i > 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ mx: 0.25 }}>/</Typography>
                          )}
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => fbGoTo(i)}
                            disabled={fbLoading || i === fbStack.length - 1}
                            sx={{
                              minWidth: 0,
                              px: 0.5,
                              py: 0,
                              textTransform: 'none',
                              fontWeight: i === fbStack.length - 1 ? 700 : 400,
                              color: i === fbStack.length - 1 ? 'text.primary' : 'primary.main',
                            }}
                          >
                            {crumb.name}
                          </Button>
                        </Box>
                      ))}
                    </Stack>

                    {fbLoading && <LinearProgress />}
                    {fbErr && (
                      <Typography variant="caption" color="error.main" sx={{ display: 'block', pl: 0.5 }}>
                        {fbErr}
                      </Typography>
                    )}
                    {!fbLoading && !fbErr && fbItems.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5, py: 0.5 }}>
                        This folder is empty.
                      </Typography>
                    )}

                    <Stack>
                      {fbItems.map((item) => (
                        <Stack
                          key={item.id}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ py: 0.5, px: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          {item.isFolder ? (
                            <FolderIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          ) : (
                            <DescriptionIcon fontSize="small" color="action" />
                          )}
                          {item.isFolder ? (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => fbEnter(item)}
                              disabled={fbLoading}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                color: 'text.primary',
                              }}
                            >
                              {item.name}
                            </Button>
                          ) : (
                            <Typography
                              variant="body2"
                              component="a"
                              href={item.webUrl || undefined}
                              target="_blank"
                              rel="noopener"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textDecoration: 'none',
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {item.name}
                            </Typography>
                          )}
                          <Tooltip title="Open in OneDrive">
                            <span>
                              <IconButton
                                size="small"
                                component="a"
                                href={item.webUrl || undefined}
                                target="_blank"
                                rel="noopener"
                                disabled={!item.webUrl}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Create the {project.status === 'won' ? 'project' : 'proposal'} folder (button above) to browse its contents here.
              </Typography>
            )
          )}
        </Paper>
      )}

      <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2} alignItems="center" justifyContent="space-between">
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Quotations</Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          <Button
            component={Link}
            to={`/sales/calcsheet/projects/${project.id}/schedule`}
            variant="outlined"
            startIcon={<CalendarMonthIcon />}
            size="small"
          >
            Gantt Chart
          </Button>
          {quotations.length >= 2 && (
            <Button
              component={Link}
              to={`/sales/calcsheet/projects/${project.id}/compare`}
              variant="outlined"
              startIcon={<CompareArrowsIcon />}
              size="small"
            >
              Compare
            </Button>
          )}
          <Button
            variant={hasIoct ? 'outlined' : 'contained'}
            startIcon={<AddIcon />}
            onClick={() => startNew('IOCT')}
            size="small"
          >
            IOCT quotation
          </Button>
          <Button
            variant={hasActi ? 'outlined' : 'contained'}
            startIcon={<AddIcon />}
            onClick={() => startNew('ACTI')}
            size="small"
            color="secondary"
          >
            ACTI quotation
          </Button>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => legacyFileInputRef.current?.click()}
            title="Import a legacy .xlsx or .pdf into this project — or just drag-and-drop the file onto this page"
          >
            Import legacy
          </Button>
          <input
            ref={legacyFileInputRef}
            type="file"
            accept=".xlsx,.xls,.pdf,application/pdf"
            hidden
            onChange={(e) => handleLegacyFile(e.target.files?.[0] ?? null)}
          />
          {/* Hidden ref retained for compatibility — no longer wired to a button */}
          <input
            ref={legacyPdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={(e) => onLegacyPdfPicked(e.target.files?.[0] ?? null)}
          />
        </Stack>
      </Stack>

      {isPhone ? (
        <Stack spacing={1}>
          {quotations.map((q) => {
            const t = computeTotals(q);
            const recipient = clients.find((c) => c.id === q.recipientId);
            return (
              <Paper key={q.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={q.kind} color={q.kind === 'IOCT' ? 'primary' : 'secondary'} />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>rev {q.revision}</Typography>
                  {q.formulaVersion === 'legacy' && (
                    <Chip size="small" icon={<HistoryIcon />} label="Legacy" color="warning" variant="outlined" sx={{ height: 20 }} />
                  )}
                  {q.awardedAt && <Chip size="small" icon={<LockIcon />} label="Awarded" color="success" sx={{ height: 20 }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {recipient?.name ?? 'No recipient'}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1} sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                      Subtotal {PHP(t.subtotal)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                      VAT {PHP(t.vat)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                      Budget {PHP(awardFigures(q).budget)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Grand total</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{PHP(t.grandTotal)}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Button size="small" variant="contained" component={Link} to={`/sales/calcsheet/quotations/${q.id}`} startIcon={<OpenInNewIcon />} sx={{ flex: 1 }}>
                    Open
                  </Button>
                  <IconButton onClick={() => setDuplicateTarget(q)} aria-label="Duplicate quotation"><ContentCopyIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => setDeleteTarget(q)} aria-label="Delete quotation" disabled={!!q.awardedAt}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
              </Paper>
            );
          })}
          {quotations.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No quotations yet. Create an IOCT or ACTI quotation to begin.</Typography>
            </Paper>
          )}
        </Stack>
      ) : (
      <Paper>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kind</TableCell>
              <TableCell>Revision</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell align="right">Subtotal</TableCell>
              <TableCell align="right">VAT</TableCell>
              <TableCell align="right">Grand Total</TableCell>
              <TableCell align="right" title="Spendable budget: VAT-ex value minus margin">Budget</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quotations.map((q) => {
              const t = computeTotals(q);
              const recipient = clients.find((c) => c.id === q.recipientId);
              return (
                <TableRow key={q.id} hover>
                  <TableCell>
                    <Chip size="small" label={q.kind} color={q.kind === 'IOCT' ? 'primary' : 'secondary'} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>rev {q.revision}</span>
                      {q.awardedAt && (
                        <Chip size="small" icon={<LockIcon />} label="Awarded" color="success" sx={{ height: 20 }} />
                      )}
                      {q.formulaVersion === 'legacy' && (
                        <Chip
                          size="small"
                          icon={<HistoryIcon />}
                          label="Legacy"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      )}
                      {q.formulaVersion === 'legacy' && (() => {
                        const src = q.importedFrom?.sourceFile || '';
                        const isPdf = /\.pdf$/i.test(src);
                        const isXlsx = /\.xlsx?$/i.test(src);
                        if (!isPdf && !isXlsx) return null;
                        return (
                          <Tooltip title={src || (isPdf ? 'Imported from PDF' : 'Imported from xlsx')}>
                            <Chip
                              size="small"
                              label={isPdf ? 'PDF' : 'XLSX'}
                              variant="outlined"
                              sx={{
                                height: 20,
                                borderColor: isPdf ? 'error.light' : 'success.light',
                                color: isPdf ? 'error.main' : 'success.main',
                                '& .MuiChip-label': { fontSize: '0.65rem', fontWeight: 600 },
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </Stack>
                  </TableCell>
                  <TableCell>{recipient?.name ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{PHP(t.subtotal)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{PHP(t.vat)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{PHP(t.grandTotal)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{PHP(awardFigures(q).budget)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" component={Link} to={`/sales/calcsheet/quotations/${q.id}`}><OpenInNewIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setDuplicateTarget(q)} title="Duplicate quotation"><ContentCopyIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setDeleteTarget(q)} disabled={!!q.awardedAt} title={q.awardedAt ? 'Awarded quotations are locked' : 'Delete quotation'}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {quotations.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No quotations yet. Create an IOCT or ACTI quotation to begin.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
      </Paper>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>Edit project details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Project code"
              value={editForm.code}
              onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))}
              fullWidth
              size="small"
              inputProps={{ style: { fontFamily: 'monospace' } }}
              placeholder={editForm.code ? undefined : 'Assigned automatically once a customer is set'}
              helperText={editForm.code ? undefined : 'No code yet — leave blank and pick a customer below to have one assigned'}
            />
            <TextField
              label="Project name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Location"
              value={editForm.location}
              onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              select
              label="Customer"
              value={editForm.customerId}
              onChange={(e) => setEditForm((f) => ({ ...f, customerId: e.target.value }))}
              fullWidth
              size="small"
            >
              <MenuItem value="">— none —</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Partner"
              value={editForm.partnerId}
              onChange={(e) => setEditForm((f) => ({ ...f, partnerId: e.target.value }))}
              fullWidth
              size="small"
            >
              <MenuItem value="">— none —</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={!editForm.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={legacyOpen} onClose={closeLegacyDialog} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <HistoryIcon color="warning" />
            <span>Import legacy quotation into this project</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {legacyImporting && <LinearProgress sx={{ mb: 2 }} />}
          {legacyError && <Alert severity="error" sx={{ mb: 2 }}>{legacyError}</Alert>}
          {!legacyParsed && !legacyError && (
            <Typography variant="body2" color="text.secondary">Parsing…</Typography>
          )}
          {legacyParsed && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Source file</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {legacySource}
                </Typography>
              </Box>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Original code</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{legacyParsed.originalCode || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Workbook customer</Typography>
                  <Typography variant="body2">
                    {legacyParsed.customer.name || '—'}
                    {legacyParsed.customer.code && (
                      <Chip size="small" label={legacyParsed.customer.code} sx={{ ml: 0.75, height: 18 }} />
                    )}
                  </Typography>
                </Box>
              </Stack>

              {legacyParsed.warnings.length > 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <Stack spacing={0.25}>
                    {legacyParsed.warnings.map((w, i) => (
                      <Typography key={i} variant="caption">{w}</Typography>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Quotations found</Typography>
                <Stack spacing={1.5}>
                  {(['IOCT', 'ACTI'] as QuotationKind[]).map((k) => {
                    const pq = legacyParsed.quotations.find((q) => q.kind === k);
                    if (!pq) return (
                      <Paper key={k} variant="outlined" sx={{ p: 1.25, opacity: 0.6 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={k} variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            not present in this workbook
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                    const dupExists = quotations.some(
                      (q) => q.kind === k && q.revision === legacyRevisions[k],
                    );
                    const includeVat = legacyIncludeVat[k];
                    const snap = pq.legacyTotalsSnapshot;
                    const grandIncl = snap.grandTotal;
                    const grandExcl = Math.max(0, snap.subtotal - snap.discount);
                    const hasVatInWorkbook = (snap.vat || 0) > 0;
                    const liveTotal = includeVat ? grandIncl : grandExcl;
                    return (
                      <Paper key={k} variant="outlined" sx={{ p: 1.25 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Checkbox
                              size="small"
                              checked={legacySelect[k]}
                              onChange={(e) => setLegacySelect((s) => ({ ...s, [k]: e.target.checked }))}
                              sx={{ p: 0.5 }}
                            />
                            <Chip
                              size="small"
                              label={k}
                              color={k === 'IOCT' ? 'primary' : 'secondary'}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                              {pq.components.length} component(s) · {pq.services.length} service(s) · {pq.generalReqts.length} gen-reqt(s)
                            </Typography>
                            <Stack alignItems="flex-end" spacing={0}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {PHP(liveTotal)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {includeVat ? 'VAT-IN' : 'VAT-EX'}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <TextField
                              label="Revision"
                              size="small"
                              value={legacyRevisions[k]}
                              onChange={(e) => setLegacyRevisions((r) => ({ ...r, [k]: e.target.value.padStart(2, '0').slice(0, 2) }))}
                              disabled={!legacySelect[k]}
                              error={legacySelect[k] && dupExists}
                              helperText={legacySelect[k] && dupExists ? 'Already exists' : ' '}
                              sx={{ width: 110 }}
                              inputProps={{ style: { fontFamily: 'monospace' } }}
                            />
                            <TextField
                              select
                              label="Recipient"
                              size="small"
                              value={legacyRecipient[k]}
                              onChange={(e) => setLegacyRecipient((r) => ({ ...r, [k]: e.target.value }))}
                              disabled={!legacySelect[k]}
                              sx={{ flex: 1 }}
                            >
                              <MenuItem value="">— none —</MenuItem>
                              {clients.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={includeVat}
                                  onChange={(e) => setLegacyIncludeVat((v) => ({ ...v, [k]: e.target.checked }))}
                                  disabled={!legacySelect[k] || !hasVatInWorkbook}
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  Include VAT
                                </Typography>
                              }
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              {hasVatInWorkbook
                                ? `VAT-EX ${PHP(grandExcl)} · VAT ${PHP(snap.vat)} · VAT-IN ${PHP(grandIncl)}`
                                : 'workbook has no VAT line — already VAT-EX'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Imported quotations are tagged <strong>legacy</strong> with frozen totals snapshot.
                Duplicate one later to create an editable revision under the current formulation.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLegacyDialog} disabled={legacyImporting}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={importLegacySelected}
            disabled={legacyImporting || !legacyParsed || (!legacySelect.IOCT && !legacySelect.ACTI)}
          >
            Import selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pdfOpen} onClose={closePdfDialog} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <HistoryIcon color="warning" />
            <span>Import legacy quotation from PDF</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {pdfParsing && <LinearProgress sx={{ mb: 2 }} />}
          {pdfError && <Alert severity="error" sx={{ mb: 2 }}>{pdfError}</Alert>}
          {!pdfParsed && !pdfParsing && !pdfError && (
            <Typography variant="body2" color="text.secondary">Pick a PDF…</Typography>
          )}
          {pdfParsed && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Source PDF</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {pdfParsed.sourceFile}
                </Typography>
              </Box>

              {pdfParsed.warnings.length > 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <Stack spacing={0.25}>
                    {pdfParsed.warnings.map((w, i) => (
                      <Typography key={i} variant="caption">{w}</Typography>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Stack direction="row" spacing={1.5}>
                <TextField
                  select
                  label="Kind"
                  size="small"
                  value={pdfForm.kind}
                  onChange={(e) => setPdfForm((f) => ({
                    ...f,
                    kind: e.target.value as QuotationKind,
                    revision: nextRevisionFor(e.target.value as QuotationKind, f.revision),
                  }))}
                  sx={{ width: 100 }}
                >
                  <MenuItem value="IOCT">IOCT</MenuItem>
                  <MenuItem value="ACTI">ACTI</MenuItem>
                </TextField>
                <TextField
                  label="Revision"
                  size="small"
                  value={pdfForm.revision}
                  onChange={(e) => setPdfForm((f) => ({ ...f, revision: e.target.value.padStart(2, '0').slice(0, 2) }))}
                  sx={{ width: 90 }}
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
                <TextField
                  label="Original code"
                  size="small"
                  value={pdfParsed.refCode}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Stack>

              <TextField
                select
                label="Recipient"
                size="small"
                value={pdfForm.recipientId}
                onChange={(e) => setPdfForm((f) => ({ ...f, recipientId: e.target.value }))}
                fullWidth
                helperText={pdfParsed.recipientName ? `Detected: ${pdfParsed.recipientName}` : ' '}
              >
                <MenuItem value="">— none —</MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
                ))}
              </TextField>

              <Stack direction="row" spacing={1.5}>
                <TextField
                  label="Date"
                  type="date"
                  size="small"
                  value={pdfForm.date}
                  onChange={(e) => setPdfForm((f) => ({ ...f, date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Validity (days)"
                  type="number"
                  size="small"
                  value={pdfForm.validityDays}
                  onChange={(e) => setPdfForm((f) => ({ ...f, validityDays: parseInt(e.target.value, 10) || 30 }))}
                  onWheel={blurNumberInputOnWheel}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="Warranty (mos)"
                  type="number"
                  size="small"
                  value={pdfForm.warrantyMonths}
                  onChange={(e) => setPdfForm((f) => ({ ...f, warrantyMonths: parseInt(e.target.value, 10) || 12 }))}
                  onWheel={blurNumberInputOnWheel}
                  sx={{ width: 120 }}
                />
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Snapshot totals</Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <TextField
                    label="A. General Reqts"
                    type="text"
                    inputMode="decimal"
                    size="small"
                    value={pdfForm.sectionA}
                    onChange={(e) => setPdfForm((f) => ({ ...f, sectionA: parseLenientFloat(e.target.value) }))}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="B. Components"
                    type="text"
                    inputMode="decimal"
                    size="small"
                    value={pdfForm.sectionB}
                    onChange={(e) => setPdfForm((f) => ({ ...f, sectionB: parseLenientFloat(e.target.value) }))}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="C. Eng. Services"
                    type="text"
                    inputMode="decimal"
                    size="small"
                    value={pdfForm.sectionC}
                    onChange={(e) => setPdfForm((f) => ({ ...f, sectionC: parseLenientFloat(e.target.value) }))}
                    sx={{ width: 160 }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                  <TextField
                    select
                    label="VAT mode"
                    size="small"
                    value={pdfForm.vatMode}
                    onChange={(e) => setPdfForm((f) => ({ ...f, vatMode: e.target.value as 'VAT-EX' | 'VAT-IN' }))}
                    sx={{ width: 140 }}
                  >
                    <MenuItem value="VAT-EX">VAT-EX (0%)</MenuItem>
                    <MenuItem value="VAT-IN">VAT-IN (12%)</MenuItem>
                  </TextField>
                  <TextField
                    label="Grand Total (PHP)"
                    type="text"
                    inputMode="decimal"
                    size="small"
                    value={pdfForm.grandTotal}
                    onChange={(e) => setPdfForm((f) => ({ ...f, grandTotal: parseLenientFloat(e.target.value) }))}
                    sx={{ flex: 1, '& input': { fontWeight: 600 } }}
                  />
                </Stack>
                {(() => {
                  const summed = pdfForm.sectionA + pdfForm.sectionB + pdfForm.sectionC;
                  const expected = pdfForm.vatMode === 'VAT-IN'
                    ? +(pdfForm.grandTotal / 1.12).toFixed(2)
                    : pdfForm.grandTotal;
                  const drift = Math.abs(summed - expected);
                  if (summed > 0 && drift > 1) {
                    return (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                        Section subtotals sum to {PHP(summed)} but grand total {pdfForm.vatMode} expects {PHP(expected)} (drift {PHP(drift)})
                      </Typography>
                    );
                  }
                  return null;
                })()}
              </Box>

              <TextField
                label="Payment terms"
                size="small"
                value={pdfForm.paymentTerms}
                onChange={(e) => setPdfForm((f) => ({ ...f, paymentTerms: e.target.value }))}
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
              />
              <TextField
                label="Delivery terms"
                size="small"
                value={pdfForm.deliveryTerms}
                onChange={(e) => setPdfForm((f) => ({ ...f, deliveryTerms: e.target.value }))}
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
              />

              <Typography variant="caption" color="text.secondary">
                PDF imports record only the snapshot totals (no line items — the original PDF is your line-item reference).
                Quotation is tagged <strong>legacy</strong>; duplicate to revise under the current formulation.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePdfDialog} disabled={pdfImporting}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={importLegacyPdf}
            disabled={pdfImporting || pdfParsing || !pdfParsed || !pdfForm.grandTotal}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New {kind} quotation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Recipient" value={recipientId} onChange={(e) => setRecipientId(e.target.value)} fullWidth>
              <MenuItem value="">— none —</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              {kind === 'IOCT'
                ? 'IOCT issues this quotation to the recipient — typically the partner (ACTI) when subcontracted, or the end customer when direct.'
                : 'ACTI issues this quotation to the end customer — typically includes hardware and IOCT services with ACTI margin.'}
            </Typography>
            <TextField
              select
              label="Scope"
              value={scopeCategory}
              onChange={(e) => setScopeCategory(e.target.value as QuotationScopeCategory)}
              fullWidth
              helperText={
                scopeCategory === 'supply'
                  ? 'Only B. Supply of Components will be editable — A and C lock.'
                  : scopeCategory === 'services'
                    ? 'Only C. Services (and Manpower) will be editable — B locks. A stays editable.'
                    : 'Everything editable — the usual full quotation.'
              }
            >
              <MenuItem value="both">Both — supply &amp; services</MenuItem>
              <MenuItem value="supply">Supply only</MenuItem>
              <MenuItem value="services">Services only</MenuItem>
            </TextField>
            <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
              Scope is fixed once this quotation is created, like Kind — create a new quotation to change it later.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lockDialogOpen} onClose={() => setLockDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>Lock the awarded price</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Pick the quotation this project was awarded on. Its price becomes the Project List contract amount, its
              budget is what spending is tracked against, and the quotation becomes read-only.
            </Typography>
            <AwardQuotationPicker quotations={quotations} clients={clients} value={awardPickId} onChange={setAwardPickId} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<LockIcon />}
            disabled={!awardPickId || awardBusy}
            onClick={async () => {
              setLockDialogOpen(false);
              await lockAward(awardPickId);
            }}
          >
            Lock price
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unlockConfirmOpen} onClose={() => setUnlockConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unlock the awarded price?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The awarded quotation becomes editable again and the price lock is removed. The Project List contract amount
            stays as it is until you lock a price again. Use this for change orders.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockConfirmOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => { void confirmUnlock(); }}>Unlock</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusConfirmOpen} onClose={() => setStatusConfirmOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>Mark proposal as won?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Which quotation was this project awarded on? Its price and budget get <strong>locked</strong>: the quotation
              can't be edited any more, and the Project List contract amount and budget come from it.
            </Typography>
            <AwardQuotationPicker quotations={quotations} clients={clients} value={awardPickId} onChange={setAwardPickId} />
            <Alert severity="info" variant="outlined">
              OneDrive execution folder is named like{' '}
              <strong>IOCT2605001-LBI Project Name</strong> when a Project List
              number is linked. Prefer &quot;Create Project&quot; or &quot;Link to Existing&quot;
              so the folder gets the IOCT number (not the PCS proposal code).
            </Alert>
            <Alert severity="info" variant="outlined">
              Changing the status back later will not delete the Project List record. You can unlink or edit it separately.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button onClick={() => setStatusConfirmOpen(false)}>Cancel</Button>
          <Button onClick={() => { void confirmWon(false); }}>Mark Won Only</Button>
          <Button
            onClick={async () => {
              setStatusConfirmOpen(false);
              if (awardPickId && !(await lockAward(awardPickId))) return;
              openLinkExistingDialog(true);
            }}
          >
            Link to Existing Project
          </Button>
          <Button variant="contained" color="success" onClick={() => { void confirmWon(true); }}>
            Mark Won and Create Project
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={poDialogOpen} onClose={() => { if (!poBusy) setPoDialogOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Attach Customer PO</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {poErr && <Alert severity="error">{poErr}</Alert>}
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              {poFile ? poFile.name : 'Choose file'}
              <input
                type="file"
                hidden
                onChange={(e) => setPoFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            <TextField
              label="PO Number"
              size="small"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              fullWidth
            />
            <TextField
              label="PO Date"
              type="date"
              size="small"
              value={poDate}
              onChange={(e) => setPoDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoDialogOpen(false)} disabled={poBusy}>
            Skip for now
          </Button>
          <Button variant="contained" onClick={() => { void attachPo(); }} disabled={poBusy || !poFile}>
            {poBusy ? 'Attaching…' : 'Attach'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!poDeleteTarget} onClose={() => { if (!poDeleteBusy) setPoDeleteTarget(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Remove this PO?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes "{poDeleteTarget?.fileName}" from the project and best-effort deletes the file from OneDrive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoDeleteTarget(null)} disabled={poDeleteBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { void deletePo(); }} disabled={poDeleteBusy}>
            {poDeleteBusy ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkExistingOpen} onClose={() => { if (!linkExistingBusy) setLinkExistingOpen(false); }} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>
          {linkExistingWon ? 'Mark Won — Link to Existing Project' : 'Link to Existing Project List Record'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {linkExistingWon && (
              <Alert severity="info" variant="outlined">
                The proposal will be marked as <strong>won</strong> and linked to the selected Project List record. No new record will be created.
              </Alert>
            )}
            <TextField
              label="Search by project no., name, or client"
              size="small"
              fullWidth
              value={linkExistingQuery}
              onChange={(e) => setLinkExistingQuery(e.target.value)}
              autoFocus
            />
            {linkExistingLoading ? (
              <Typography variant="body2" color="text.secondary">Loading projects…</Typography>
            ) : (
              <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {linkExistingProjects
                  .filter((p) => {
                    const q = linkExistingQuery.toLowerCase();
                    if (!q) return true;
                    return (
                      (p.project_no || '').toLowerCase().includes(q) ||
                      (p.project_name || '').toLowerCase().includes(q) ||
                      (p.account_name || '').toLowerCase().includes(q)
                    );
                  })
                  .map((p) => (
                    <Box
                      key={p.id}
                      onClick={() => { if (!linkExistingBusy) void linkToExistingProject(p.id); }}
                      sx={{
                        px: 2, py: 1.25, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                        '&:last-child': { borderBottom: 0 },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>{p.project_no || '—'}</Typography>
                      <Typography variant="body2">{p.project_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.account_name}</Typography>
                    </Box>
                  ))}
                {!linkExistingLoading && linkExistingProjects.filter((p) => {
                  const q = linkExistingQuery.toLowerCase();
                  if (!q) return true;
                  return (p.project_no || '').toLowerCase().includes(q) || (p.project_name || '').toLowerCase().includes(q) || (p.account_name || '').toLowerCase().includes(q);
                }).length === 0 && (
                  <Box sx={{ px: 2, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">No matching projects found.</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkExistingOpen(false)} disabled={linkExistingBusy}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <DuplicateQuotationDialog
        open={!!duplicateTarget}
        quotation={duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        onDuplicated={(copy) => {
          setDuplicateTarget(null);
          navigate(`/sales/calcsheet/quotations/${copy.id}`);
        }}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete quotation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Permanently delete the <strong>{deleteTarget?.kind} rev {deleteTarget?.revision}</strong> quotation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (deleteTarget) { deleteQuotation(deleteTarget.id); setDeleteTarget(null); }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link existing OneDrive folder dialog */}
      <Dialog open={!!linkDialogOpen} onClose={() => !linkBusy && setLinkDialogOpen(null)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>
          Link existing {linkDialogOpen} folder
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Suggested folders — auto-scanned by PCS code prefix */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {project ? (
                  <>Suggested folders in <code>{linkDialogOpen === 'proposal' ? onedriveConfig.proposalRoot : onedriveConfig.executionRoot}</code> starting with <code>{projectCodePrefix(project)}</code>:</>
                ) : 'Suggested folders:'}
              </Typography>
              {linkSuggestionsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <LinearProgress sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">Scanning…</Typography>
                </Box>
              ) : linkSuggestions.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                  No folders found with this project&apos;s PCS code prefix. Paste a URL below to link manually.
                </Alert>
              ) : (
                <Stack spacing={0.5}>
                  {linkSuggestions.map((s) => (
                    <Paper
                      key={s.id}
                      variant="outlined"
                      sx={{
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: linkBusy ? 'wait' : 'pointer',
                        '&:hover': { bgcolor: linkBusy ? undefined : 'action.hover' },
                      }}
                      onClick={() => !linkBusy && linkToSuggestion(s)}
                    >
                      <FolderIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                        {s.name}
                      </Typography>
                      <Button size="small" variant="contained" disabled={linkBusy}>
                        Link this
                      </Button>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider>or paste a URL</Divider>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                For folders that don&apos;t match the PCS code prefix, paste the OneDrive URL (long Documents path or short share link).
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="OneDrive folder URL"
                placeholder="https://iocontroltech-my.sharepoint.com/..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                disabled={linkBusy}
                error={!!linkErr}
                helperText={linkErr || ' '}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(null)} disabled={linkBusy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitLinkDialog}
            disabled={linkBusy || !linkUrl.trim()}
          >
            {linkBusy ? 'Linking…' : 'Link from URL'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
    </Box>
  );
}
